/**
 * DonutChart Component
 *
 * Gráfico donut para distribuição de categorias.
 * legendMode: 'inline' (legenda lateral) | 'floating' (apenas gráfico + interação flutuante)
 */

import React, { useState, useMemo, useRef, useEffect } from 'react'
import { List } from 'lucide-react'
import './DonutChart.css'

function DonutChart({ data = [], onItemClick, legendMode = 'inline' }) {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const popoverRef = useRef(null)
  const isFloating = legendMode === 'floating'

  useEffect(() => {
    if (!popoverOpen) return undefined

    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setPopoverOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [popoverOpen])

  const donutData = useMemo(() => {
    let currentOffset = 0
    const radius = 70
    const circumference = 2 * Math.PI * radius

    return data.map((item) => {
      const strokeDasharray = `${((item.percentage || 0) * circumference) / 100} ${circumference}`
      const strokeDashoffset = -currentOffset
      currentOffset += ((item.percentage || 0) * circumference) / 100
      return {
        ...item,
        strokeDasharray,
        strokeDashoffset
      }
    })
  }, [data])

  const totalAmount = useMemo(
    () => data.reduce((sum, item) => sum + (item.amount || 0), 0),
    [data]
  )

  if (!data || data.length === 0) {
    return (
      <div className="donut-chart-empty">
        <p>Nenhum dado disponível</p>
      </div>
    )
  }

  const formatCurrency = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)

  const hoveredItem = hoveredIndex !== null ? donutData[hoveredIndex] : null

  const handleItemClick = (item) => {
    if (onItemClick) onItemClick(item)
    if (isFloating) setPopoverOpen(false)
  }

  const renderLegendItem = (item, i) => (
    <div
      key={item.id || i}
      className={`donut-chart-legend-item ${hoveredIndex === i ? 'active' : ''} ${hoveredIndex !== null && hoveredIndex !== i ? 'dimmed' : ''}`}
      onMouseEnter={() => setHoveredIndex(i)}
      onMouseLeave={() => setHoveredIndex(null)}
      onClick={() => handleItemClick(item)}
    >
      <div className="donut-chart-legend-color" style={{ backgroundColor: item.color || '#16a34a' }} />
      <div className="donut-chart-legend-content">
        <span className="donut-chart-legend-label">{item.name}</span>
        <span className="donut-chart-legend-amount">{formatCurrency(item.amount || 0)}</span>
      </div>
      <span className="donut-chart-legend-percentage">{item.percentage?.toFixed(0) || 0}%</span>
    </div>
  )

  return (
    <div className={`donut-chart${isFloating ? ' donut-chart--floating' : ''}`}>
      <div className="donut-chart-visual">
        <svg viewBox="0 0 160 160" className="donut-chart-svg">
          {donutData.map((item, i) => (
            <circle
              key={item.id || i}
              cx="80"
              cy="80"
              r="70"
              fill="transparent"
              stroke={item.color || '#16a34a'}
              strokeWidth={hoveredIndex === i ? 20 : 14}
              strokeDasharray={item.strokeDasharray}
              strokeDashoffset={item.strokeDashoffset}
              className="donut-chart-segment"
              style={{
                transform: 'rotate(-90deg)',
                transformOrigin: '80px 80px'
              }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => handleItemClick(item)}
            />
          ))}
        </svg>
        <div className="donut-chart-center">
          {isFloating && hoveredItem ? (
            <>
              <p className="donut-chart-center-value">{hoveredItem.percentage?.toFixed(0) || 0}%</p>
              <p className="donut-chart-center-label">{hoveredItem.name}</p>
            </>
          ) : isFloating ? (
            <>
              <p className="donut-chart-center-value">{data.length}</p>
              <p className="donut-chart-center-label">categorias</p>
            </>
          ) : (
            <p className="donut-chart-center-value">100%</p>
          )}
        </div>

        {isFloating && hoveredItem && (
          <div className="donut-chart-floating-tooltip" role="tooltip">
            <span className="donut-chart-floating-tooltip-name">{hoveredItem.name}</span>
            <span className="donut-chart-floating-tooltip-amount">{formatCurrency(hoveredItem.amount || 0)}</span>
            <span className="donut-chart-floating-tooltip-percent">{hoveredItem.percentage?.toFixed(1) || 0}%</span>
          </div>
        )}

        {isFloating && (
          <div className="donut-chart-floating-actions" ref={popoverRef}>
            <button
              type="button"
              className="donut-chart-list-btn"
              onClick={() => setPopoverOpen((open) => !open)}
              aria-expanded={popoverOpen}
              aria-label="Ver lista de categorias"
              title="Ver categorias"
            >
              <List size={14} />
              <span>Categorias</span>
            </button>
            {popoverOpen && (
              <div className="donut-chart-categories-popover">
                <div className="donut-chart-categories-popover-header">
                  <span>OPEX — {formatCurrency(totalAmount)}</span>
                </div>
                <div className="donut-chart-categories-popover-list">
                  {donutData.map((item, i) => renderLegendItem(item, i))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!isFloating && (
        <div className="donut-chart-legend">
          {donutData.map((item, i) => renderLegendItem(item, i))}
        </div>
      )}
    </div>
  )
}

export default DonutChart
